import { useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  TextField,
  InputAdornment,
  Stack,
  Paper,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useCashRegisterSession } from 'src/hooks/use-cash-register-session';
import { FCurrency } from 'src/utils/format-number';
import { getPaymentMethodIcon, getPaymentMethodLabel } from 'src/utils/payment-methods';

// ----------------------------------------------------------------------

export default function CashRegisterCloseButton() {
  const { t } = useLocales();
  const theme = useTheme();
  const { session, handleCloseSessionOnly, isCashier } = useCashRegisterSession();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [closingAmount, setClosingAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Compute expected amount — use backend values first
  const expectedAmount = useMemo(() => {
    if (!session) return 0;
    // Use backend-computed currentAmount if available
    if (session.currentAmount !== undefined && session.currentAmount !== null) {
      return session.currentAmount;
    }
    // Use backend-computed expectedAmount if available
    if (session.expectedAmount !== undefined && session.expectedAmount !== null) {
      return session.expectedAmount;
    }
    // Fallback: calculate from transactions
    const transactions = session.transactions || [];
    const totalSales = transactions
      .filter((t: any) => t.type === 'sale' || t.type === 'deposit')
      .reduce((sum: number, t: any) => sum + t.amount, 0);
    const totalExpenses = transactions
      .filter((t: any) => t.type === 'withdrawal' || t.type === 'refund')
      .reduce((sum: number, t: any) => sum + t.amount, 0);
    return (session.openingAmount || 0) + totalSales - totalExpenses;
  }, [session]);

  // Payment methods breakdown — prioritize backend data
  const paymentMethods = useMemo(() => {
    if (!session) return [];

    // 1. Use explicit paymentMethods array from backend (most reliable)
    if (session.paymentMethods && session.paymentMethods.length > 0) {
      return session.paymentMethods.filter((pm: any) => pm.amount > 0);
    }

    // 2. Fallback: use totalSales as cash
    if (session.totalSales && session.totalSales > 0) {
      return [{ method: 'cash', amount: session.totalSales }];
    }

    // 3. Last resort: compute from transactions
    const breakdown: Record<string, number> = {};
    const transactions = session.transactions || [];
    transactions.forEach((t: any) => {
      if (t.type === 'sale' || t.type === 'deposit') {
        const m = t.paymentMethod || 'cash';
        breakdown[m] = (breakdown[m] || 0) + t.amount;
      }
    });
    return Object.entries(breakdown)
      .filter(([, amount]) => (amount as number) > 0)
      .map(([method, amount]) => ({ method, amount }));
  }, [session]);

  const discrepancy = closingAmount ? parseFloat(closingAmount) - expectedAmount : 0;

  // Only show for cashiers with an active session
  if (!isCashier || !session || session.status !== 'open') {
    return null;
  }

  const handleOpenDialog = () => {
    setClosingAmount(expectedAmount.toString());
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!submitting) {
      setDialogOpen(false);
      setSubmitError(null);
    }
  };

  const handleConfirmClose = async () => {
    const amount = parseFloat(closingAmount);
    if (Number.isNaN(amount) || amount < 0) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await handleCloseSessionOnly(amount);
      setDialogOpen(false);
    } catch (err: any) {
      setSubmitError(err.message || t('cash_register.errors.session_close_error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Theme colors
  const headerBg = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
  const summaryBg = alpha(theme.palette.primary.main, 0.04);
  const summaryBorder = alpha(theme.palette.primary.main, 0.15);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={handleOpenDialog}
        startIcon={<Iconify icon="eva:lock-fill" width={16} />}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          borderColor: alpha(theme.palette.primary.main, 0.4),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('cash_register.close_session')}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: 'hidden' },
        }}
      >
        {/* Header — uses theme primary color */}
        <Box
          sx={{
            p: 3,
            background: headerBg,
            color: 'white',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.common.white, 0.2),
                display: 'flex',
              }}
            >
              <Iconify icon="eva:clipboard-fill" width={28} height={28} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {t('cash_register.dialogs.close.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('cash_register.dialogs.close.description')}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {/* Session summary */}
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              bgcolor: summaryBg,
              borderColor: summaryBorder,
            }}
          >
            <Stack spacing={1.5}>
              {/* Opening amount with description */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash_register.opening_amount')}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t('cash_register.session_dialog.opening_amount_hint') || 'Montant de départ en caisse'}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {FCurrency(session.openingAmount)}
                </Typography>
              </Stack>

              {/* Payment methods */}
              {paymentMethods.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="body2" fontWeight="medium" color="text.secondary">
                    {t('cash_register.session_dialog.payment_methods_detail')}
                  </Typography>
                  <Stack spacing={0.75}>
                    {paymentMethods.map((pm: any) => (
                      <Stack
                        key={pm.method}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify
                            icon={getPaymentMethodIcon(pm.method)}
                            width={16}
                            color={theme.palette.text.secondary}
                          />
                          <Typography variant="body2">
                            {getPaymentMethodLabel(pm.method, t)}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" fontWeight="medium">
                          {FCurrency(pm.amount)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}

              <Divider />

              {/* Expected amount with description */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash_register.expected_amount')}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t('cash_register.session_dialog.expected_amount_hint') || 'Montant théorique en caisse (ouverture + encaissements)'}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
                  {FCurrency(expectedAmount)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* Closing amount input */}
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
            {t('cash_register.dialogs.close.amount_label')}
          </Typography>
          <TextField
            fullWidth
            autoFocus
            type="number"
            value={closingAmount}
            onChange={(e) => setClosingAmount(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:credit-card-fill" />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />

          {/* Discrepancy alert */}
          {closingAmount && discrepancy !== 0 && (
            <Alert
              severity={discrepancy > 0 ? 'success' : 'error'}
              sx={{ mt: 2, borderRadius: 2 }}
            >
              <Typography variant="body2">
                {discrepancy > 0
                  ? t('cash_register.session_dialog.surplus', { amount: FCurrency(Math.abs(discrepancy)) })
                  : t('cash_register.session_dialog.deficit', { amount: FCurrency(Math.abs(discrepancy)) })}
              </Typography>
            </Alert>
          )}

          {submitError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} disabled={submitting} sx={{ borderRadius: 2 }}>
            {t('common.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleConfirmClose}
            loading={submitting}
            disabled={!closingAmount || parseFloat(closingAmount) < 0}
            sx={{ borderRadius: 2 }}
            startIcon={<Iconify icon="eva:lock-fill" />}
          >
            {t('cash_register.dialogs.close.action')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
